import { MouseEvent, ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CoreBox, CoreTooltip, CoreTypography } from '@youscience/core';
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  SelectChangeEvent,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { RoleEnum, FormActionsStatus } from '@youscience/commonapp-service-common';

import { Chip } from '@components/Chip';
import { Search } from '@components/Search';

import { getStudentDetailsPath } from '@constants/ROUTES';
import { HEAD_CELLS, STATUS_CHIP_COLORS } from '@constants/recommenderWorkspace';
import { ALL_TYPES_VALUE, ECP_HASH } from '@constants';

import useRecommenderWorkspaceStore, {
  ApplicationStatusFilter,
  TableSearchParams,
} from '@store/RecommenderWorkspaceStore';

import { formatDateWithFallback } from '@utils/formatter';

import { ApplicantFormRequests } from '@interfaces/recommenderWorkspace';
import { Order } from '@interfaces';

import WarningIcon from '@images/Recommender/warning.svg?react';

import {
  sxStyles,
  StyledToolbar,
  StyledTotalStudents,
  StyledActionsRequiredCell,
  StyledFilterSelect,
} from './Table.styles';

const MAX_APPLICATION_DEADLINE = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

interface TableProps {
  role: RoleEnum;
  tableData: ApplicantFormRequests[];
}

export const Table = (props: TableProps) => {
  const { tableData, role } = props;

  const navigate = useNavigate();

  const [showMoreStatus, setShowMoreStatus] = useState<Record<number, boolean>>({});

  const { tableSearchParams, setTableSearchParams, getApplicantFormsAvailable, isLoadingApplicantFormsAvailable } =
    useRecommenderWorkspaceStore((store) => store);

  const updateSearchParameters = (params: Partial<TableSearchParams>) => {
    setTableSearchParams(params);

    void getApplicantFormsAvailable(role);
  };

  const handleRequestSort = (property: keyof ApplicantFormRequests) => () => {
    const isAsc = tableSearchParams.sortOrderBy === property && tableSearchParams.sortOrder === Order.asc;

    updateSearchParameters({ sortOrder: isAsc ? Order.desc : Order.asc, sortOrderBy: property });
  };

  const onBodyRowClick = (applicantId: number) => () => {
    const isFromEcp = location.hash === ECP_HASH;

    navigate({ pathname: getStudentDetailsPath(applicantId), hash: role, search: `isFromEcp=${isFromEcp}` });
  };

  const onShowMoreClick = (applicantId: number) => (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    setShowMoreStatus({ [applicantId]: Boolean(!showMoreStatus[applicantId]) });
  };

  const handleChangeApplicationFilter = (event: SelectChangeEvent<unknown>) => {
    updateSearchParameters({ applicationStatusFilter: event.target.value as ApplicationStatusFilter });
  };

  const handleSearch = (value: string) => updateSearchParameters({ searchValue: value });

  const handleResetSearch = () => updateSearchParameters({ searchValue: undefined });

  return (
    <>
      <StyledToolbar>
        <CoreBox sx={sxStyles.flexAlignPosition}>
          <CoreTypography>Application status:</CoreTypography>

          <StyledFilterSelect
            data-testid="filterByStatus"
            value={tableSearchParams.applicationStatusFilter}
            onChange={handleChangeApplicationFilter}
          >
            <MenuItem value={ALL_TYPES_VALUE}>All</MenuItem>

            {Object.values(FormActionsStatus).map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </StyledFilterSelect>
        </CoreBox>

        <CoreBox sx={sxStyles.flexAlignPosition}>
          <StyledTotalStudents>{`Total number of students: ${tableData?.length ?? 0}`}</StyledTotalStudents>

          <CoreBox sx={sxStyles.searchWrap}>
            <Search
              filterValue={tableSearchParams.searchValue}
              onEnterSearch={handleSearch}
              onClear={handleResetSearch}
            />
          </CoreBox>
        </CoreBox>
      </StyledToolbar>

      <Paper sx={sxStyles.paper}>
        <TableContainer>
          <MuiTable aria-labelledby="recommender-table">
            <TableHead>
              <TableRow sx={sxStyles.headRow}>
                {HEAD_CELLS.map((headCell) => {
                  if (headCell.id === 'actionsRequired') {
                    return (
                      <TableCell key={headCell.id} align="left">
                        {headCell.label}
                      </TableCell>
                    );
                  } else {
                    return (
                      <TableCell
                        key={headCell.id}
                        sortDirection={
                          tableSearchParams.sortOrderBy === headCell.id ? tableSearchParams.sortOrder : false
                        }
                      >
                        <TableSortLabel
                          active={tableSearchParams.sortOrderBy === headCell.id}
                          direction={
                            tableSearchParams.sortOrderBy === headCell.id ? tableSearchParams.sortOrder : Order.asc
                          }
                          onClick={handleRequestSort(headCell.id as keyof ApplicantFormRequests)}
                        >
                          {headCell.label}

                          {tableSearchParams.sortOrderBy === headCell.id ? (
                            <CoreBox component="span" sx={visuallyHidden}>
                              {tableSearchParams.sortOrder === Order.desc ? 'sorted descending' : 'sorted ascending'}
                            </CoreBox>
                          ) : null}
                        </TableSortLabel>
                      </TableCell>
                    );
                  }
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {tableData?.length === 0 ? (
                <TableRow sx={sxStyles.tableBodyRow}>
                  <TableCell colSpan={5}>
                    <CoreTypography sx={{ textAlign: 'center' }}>No data available</CoreTypography>
                  </TableCell>
                </TableRow>
              ) : null}

              {tableData?.map((row) => {
                const preparedActionsRequired = showMoreStatus[row.applicantId]
                  ? row.actionsRequired
                  : row.actionsRequired.slice(0, 2);

                return (
                  <TableRow key={row.applicantId} sx={sxStyles.tableBodyRow} onClick={onBodyRowClick(row.applicantId)}>
                    <TableCell sx={{ minWidth: '180px' }}>{row.legalName}</TableCell>

                    <TableCell>
                      <CoreBox sx={sxStyles.deadlineWrap}>
                        {formatDateWithFallback(row?.deadline, '—')}

                        {new Date(row.deadline as string) >= new Date() &&
                        new Date(row.deadline as string) <= MAX_APPLICATION_DEADLINE ? (
                          <CoreTooltip title="Deadline is within next 7 days!" disableInteractive>
                            <CoreBox>
                              <WarningIcon />
                            </CoreBox>
                          </CoreTooltip>
                        ) : null}
                      </CoreBox>
                    </TableCell>

                    <StyledActionsRequiredCell>
                      {row.status === FormActionsStatus.SUBMITTED ? (
                        <Chip title="Actions completed" size="small" />
                      ) : (
                        <>
                          {preparedActionsRequired.map((action, index) => (
                            <Chip key={action + index} title={action} size="small" />
                          ))}

                          {row.actionsRequired.length > 2 ? (
                            <CoreTypography sx={sxStyles.showMore} onClick={onShowMoreClick(row.applicantId)}>
                              {showMoreStatus[row.applicantId] ? 'less' : `+${row.actionsRequired.length - 2} more`}
                            </CoreTypography>
                          ) : null}
                        </>
                      )}
                    </StyledActionsRequiredCell>

                    <TableCell>
                      <Chip title={row.status} size="small" color={STATUS_CHIP_COLORS[row.status]} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </MuiTable>
        </TableContainer>
      </Paper>

      {isLoadingApplicantFormsAvailable ? (
        <CoreBox data-testid="progressIndicator" sx={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
          <CircularProgress size={50} thickness={3} />
        </CoreBox>
      ) : null}
    </>
  );
};
